import { COMM_WS_URL } from './realtimeConfig';

/**
 * 소통 프레젠테이션(임시 방) 클라이언트 — Cloudflare Worker `cp-realtime` 의 CommRoom 사용.
 * 주기 ping 없음: 예기치 않게 끊기면 1회만 자동 재시도, 이후 '다시 연결' 버튼(reconnect).
 */
export const COMM_HTTP_URL = COMM_WS_URL.replace(/^ws/i, 'http');
export const isCommAvailable = () => true;
export const COMM_MAX_STUDENTS = 30;

const SUBPROTOCOL = 'cp-rt.v1';
const CLOSE_REPLACED = 4000;
const CLOSE_UNAUTHORIZED = 4401;
const CLOSE_FORBIDDEN = 4403;
const CLOSE_ENDED = 4404;
const CLOSE_FULL = 4409;
const CLOSE_LOCKED = 4423;

export type CommRoomPublic = { className: string; teacherName: string; expiresAt: number; pres?: string };
export type CommItem = {
  kind: 'notice' | 'question' | 'caption';
  id: string;
  text: string;
  at: number;
  closed?: boolean;
  durationSec?: number | null;
  questionType?: 'short' | 'ox' | 'choice' | 'essay';
  choices?: string[];
  /** 선택형: 정답이 여러 개라 여러 개 골라 제출 */
  multiSelect?: boolean;
  /** 보낸 수업 프레젠 키 */
  pres?: string;
  /** 질문 출처 — 없으면 '질문'만 표시 */
  src?: 'page' | 'remote';
};
/** number: 명단 입장(번호 + 개인 입장번호)으로 들어온 학생의 번호 */
export type CommMember = { memberId: string; nickname: string; number?: number; joinedAt?: number; online: boolean };
export type CommAnswer = { memberId: string; nickname: string; number?: number; content: string; choiceIndex?: number; choiceIndexes?: number[]; at: number };
export type CommStatus = 'connecting' | 'connected' | 'reconnecting' | 'disconnected' | 'ended' | 'error';

export async function createCommRoom(
  token: string,
  input: { className: string; teacherName: string; entryCode: string }
): Promise<{ roomId: string; expiresAt: number } | { error: string }> {
  try {
    const r = await fetch(`${COMM_HTTP_URL}/comm`, {
      method: 'POST',
      headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
      body: JSON.stringify(input),
    });
    const j = (await r.json().catch(() => ({}))) as Record<string, unknown>;
    if (!r.ok) return { error: String(j.error ?? `http_${r.status}`) };
    return { roomId: String(j.roomId), expiresAt: Number(j.expiresAt) };
  } catch {
    return { error: 'network' };
  }
}

export async function fetchCommInfo(
  roomId: string
): Promise<(CommRoomPublic & { studentCount: number; maxStudents: number; rosterMode?: boolean }) | { error: string }> {
  try {
    const r = await fetch(`${COMM_HTTP_URL}/comm/${encodeURIComponent(roomId)}`, { cache: 'no-store' });
    const j = (await r.json().catch(() => ({}))) as Record<string, unknown>;
    if (!r.ok) return { error: String(j.error ?? `http_${r.status}`) };
    return j as unknown as CommRoomPublic & { studentCount: number; maxStudents: number; rosterMode?: boolean };
  } catch {
    return { error: 'network' };
  }
}

export const commStudentLink = (roomId: string) => {
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  return `${window.location.origin}${base}/comm/${roomId}`;
};

export const commRandomId = () => {
  const b = new Uint8Array(12);
  crypto.getRandomValues(b);
  return Array.from(b, (x) => x.toString(36).padStart(2, '0')).join('').slice(0, 20);
};

export interface CommSocket {
  send: (frame: Record<string, unknown>) => boolean;
  reconnect: () => void;
  close: () => void;
}

/** 서버 프레임 중 "정상 연결됨" 으로 보는 것 (자동 재시도 횟수 초기화) */
const READY_FRAMES = new Set(['welcome', 'hello', 'joined']);

export function openCommSocket(opts: {
  roomId: string;
  role: 'teacher' | 'student';
  /** 교사: Supabase access token */
  getToken?: () => Promise<string>;
  onFrame: (f: Record<string, unknown>) => void;
  onStatus: (s: CommStatus, detail?: string) => void;
}): CommSocket {
  let ws: WebSocket | null = null;
  let closed = false;
  let halted = false;
  let autoRetried = false;
  let authRetried = false;
  let retryTimer: number | null = null;

  const detach = () => {
    const s = ws;
    ws = null;
    if (!s) return;
    s.onopen = s.onmessage = s.onclose = s.onerror = null;
    try {
      s.close(1000, 'client');
    } catch {
      /* ignore */
    }
  };

  const retryOnce = () => {
    if (closed || halted || retryTimer != null) return;
    if (autoRetried) {
      opts.onStatus('disconnected');
      return;
    }
    autoRetried = true;
    opts.onStatus('reconnecting');
    retryTimer = window.setTimeout(() => {
      retryTimer = null;
      void connect();
    }, 1000);
  };

  const connect = async () => {
    if (closed || halted) return;
    detach();
    opts.onStatus(autoRetried ? 'reconnecting' : 'connecting');
    const protocols = [SUBPROTOCOL];
    if (opts.role === 'teacher') {
      const t = (await opts.getToken?.().catch(() => '')) ?? '';
      if (!t) {
        opts.onStatus('error', 'login_required');
        return;
      }
      protocols.push(`auth.${t}`);
    }
    if (closed || halted) return;
    let s: WebSocket;
    try {
      s = new WebSocket(
        `${COMM_WS_URL}/ws/comm/${encodeURIComponent(opts.roomId)}?role=${opts.role}`,
        protocols
      );
    } catch {
      retryOnce();
      return;
    }
    ws = s;
    s.onmessage = (ev) => {
      if (ws !== s || typeof ev.data !== 'string') return;
      let f: Record<string, unknown>;
      try {
        f = JSON.parse(ev.data) as Record<string, unknown>;
      } catch {
        return;
      }
      if (!f || typeof f !== 'object') return;
      if (READY_FRAMES.has(String(f.t))) {
        autoRetried = false;
        authRetried = false;
        if (f.t !== 'hello') opts.onStatus('connected');
      }
      opts.onFrame(f);
    };
    s.onclose = (ev) => {
      if (ws !== s) return;
      ws = null;
      if (closed) return;
      if (ev.code === CLOSE_ENDED) {
        halted = true;
        opts.onStatus('ended', ev.reason);
        return;
      }
      if (ev.code === CLOSE_FORBIDDEN || ev.code === CLOSE_FULL || ev.code === CLOSE_LOCKED) {
        halted = true;
        opts.onStatus('error', ev.reason || String(ev.code));
        return;
      }
      if (ev.code === CLOSE_REPLACED) {
        halted = true;
        opts.onStatus('disconnected', 'replaced');
        return;
      }
      if (ev.code === CLOSE_UNAUTHORIZED && opts.role === 'teacher' && !authRetried) {
        authRetried = true;
        void connect();
        return;
      }
      retryOnce();
    };
    s.onerror = () => {
      /* close 에서 처리 */
    };
  };

  void connect();

  return {
    send: (frame) => {
      if (!ws || ws.readyState !== WebSocket.OPEN) return false;
      try {
        ws.send(JSON.stringify(frame));
        return true;
      } catch {
        return false;
      }
    },
    reconnect: () => {
      if (closed) return;
      if (ws && ws.readyState === WebSocket.CONNECTING) return;
      if (retryTimer != null) {
        window.clearTimeout(retryTimer);
        retryTimer = null;
      }
      halted = false;
      autoRetried = false;
      authRetried = false;
      void connect();
    },
    close: () => {
      closed = true;
      if (retryTimer != null) window.clearTimeout(retryTimer);
      detach();
    },
  };
}

/** 교사 쪽 채점 (학생에게는 정답을 보내지 않음) */
export type CommAnswerKey = { ox?: 'O' | 'X'; choiceIndex?: number; choiceIndexes?: number[]; texts?: string[] };
const norm = (s: string) => s.toLowerCase().replace(/\s+/g, '').trim();
export function gradeCommAnswer(item: CommItem, key: CommAnswerKey | undefined, a: CommAnswer): boolean | null {
  if (!key || item.questionType === 'essay') return null; // 서술형: 채점 없음
  if (item.questionType === 'ox') return key.ox ? a.content.trim().toUpperCase() === key.ox : null;
  if (item.questionType === 'choice') {
    // 복수 정답: 고른 집합이 정답 집합과 정확히 같아야 정답
    const sortU = (v: number[]) => Array.from(new Set(v)).sort((x, y) => x - y);
    const want = sortU(key.choiceIndexes?.length ? key.choiceIndexes : key.choiceIndex != null ? [key.choiceIndex] : []);
    if (!want.length) return null;
    const got = sortU(a.choiceIndexes?.length ? a.choiceIndexes : a.choiceIndex != null ? [a.choiceIndex] : []);
    return got.length === want.length && got.every((v, i) => v === want[i]);
  }
  const texts = (key.texts ?? []).map(norm).filter(Boolean);
  return texts.length ? texts.includes(norm(a.content)) : null;
}

/**
 * 교사 화면용 표시 이름.
 * - 명단 입장: "3번" + (교사 쪽 명단 이름이 있으면) " 홍길동" — 이름은 교사 기기에서만 붙이고 학생에게 보내지 않음
 * - 방 입장번호: 학생이 입력한 이름
 * (예전 서버/클라이언트의 "3번 별칭" 형태도 그대로 표시)
 */
export const commMemberLabel = (m: { nickname: string; number?: number }, rosterName?: string) => {
  if (!m.number) return m.nickname;
  const base = `${m.number}번`;
  const extra = (rosterName ?? '').trim() || (m.nickname && m.nickname !== base ? m.nickname : '');
  return extra ? `${base} ${extra}` : base;
};
